"use client";

import { UserRole } from "@prisma/client";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { LuPlus } from "react-icons/lu";

import { DashboardNav } from "@/components/dashboard/dashboard-nav";
import { UserNav } from "@/components/dashboard/user-nav";
import Logo from "@/components/icons/logo";
import { ProductSelector } from "@/components/product/product-selector";
import SharedSidebar, {
  SharedSidebarFooter,
} from "@/components/shared-sidebar";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import { TreeDropdown } from "@/components/ui/tree-dropdown";
import { appAdminPrimaryNav, appDashboarPrimaryNav } from "@/constants/data";
import { useChatSwitch } from "@/hooks/use-chat-switch";
import { useLoggedUser } from "@/hooks/use-logged-user";
import { treeNodesToTreeItems, useOrgTree } from "@/hooks/use-org-tree";
import { useProjectNavItems } from "@/hooks/use-work-items";
import { isValidOrgId } from "@/lib/utils";

export type MobileMoreSheetProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
};

export function MobileMoreSheet({
  open,
  onOpenChange,
}: MobileMoreSheetProps): JSX.Element {
  const session = useSession();
  const { orgId } = useLoggedUser();
  const { selectedView } = useChatSwitch();
  const nextView = selectedView === "chat" ? "app" : selectedView;
  const { treeData, isPending, selectedOrg, goToOrg } = useOrgTree({});
  const { projectItems, isLoading, isError, fetchWorkItems } =
    useProjectNavItems();

  const isAdmin =
    session?.data?.user?.role === UserRole.ADMIN ||
    session?.data?.user?.role === UserRole.SUPER_ADMIN;

  const validOrgId = isValidOrgId(orgId) ? orgId : null;
  const dashboardHref = validOrgId
    ? `/${validOrgId}/dashboard?view=${nextView}`
    : "/dashboard";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="max-h-[85vh] !px-0"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="space-y-4 overflow-y-auto py-4">
          <div className="px-3 py-2">
            <Link
              href={dashboardHref}
              className="relative mb-4 flex items-center"
              aria-label="Dashboard"
              onClick={() => {
                onOpenChange(false);
              }}
            >
              <Logo />
            </Link>
            <SheetTitle className="sr-only">More</SheetTitle>
            <TreeDropdown
              data={treeNodesToTreeItems(treeData)}
              onSelect={(item) => {
                goToOrg(item.id || "");
                onOpenChange(false);
              }}
              selectedItemName={selectedOrg?.name}
              loading={isPending}
            />
            <ProductSelector className="mt-2 px-3" />
            <div className="space-y-1">
              <div className="-mx-3 mt-2">
                <SharedSidebar />
              </div>
              {isAdmin ? (
                <DashboardNav
                  items={appAdminPrimaryNav.items}
                  setOpen={(value) => {
                    onOpenChange(
                      typeof value === "function" ? value(open) : value,
                    );
                  }}
                />
              ) : (
                <>
                  <div className="mb-4 flex items-center justify-between px-6">
                    {appDashboarPrimaryNav.label ? (
                      <h2 className="text-lg font-semibold tracking-tight">
                        {appDashboarPrimaryNav.label}
                      </h2>
                    ) : null}
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-1"
                      type="button"
                    >
                      <LuPlus className="size-4" />
                    </Button>
                  </div>
                  {isLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <p className="text-sm text-muted-foreground">
                        Loading projects...
                      </p>
                    </div>
                  ) : isError ? (
                    <div className="flex flex-col items-center justify-center gap-2 py-4">
                      <p className="text-sm text-muted-foreground">
                        Failed to load work items
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        onClick={() => {
                          fetchWorkItems();
                        }}
                      >
                        Retry
                      </Button>
                    </div>
                  ) : projectItems?.length ? (
                    <DashboardNav
                      className="mb-2 px-3"
                      items={projectItems}
                      setOpen={(value) => {
                        onOpenChange(
                          typeof value === "function" ? value(open) : value,
                        );
                      }}
                    />
                  ) : (
                    <div className="flex items-center justify-center py-4">
                      <p className="text-sm text-muted-foreground">
                        No Projects Available
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="mt-4 flex items-center border-t px-3 pt-4">
              <UserNav />
            </div>
            <div className="mt-2 px-1">
              <SharedSidebarFooter />
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
